<template>
  <card-dialog
    v-model="modalVisible"
    :title="$t('new_diagnostic_results.dialog.header')"
    max-width="700px"
  >
    <template v-slot:title>
      <v-card-title
        class="blue-grey lighten-5 px-6 py-4"
      >
        <v-row dense>
          <v-col>
            {{ $t('new_diagnostic_results.dialog.header') }}
          </v-col>
        </v-row>
        <v-row dense>
          <v-col>
            <v-text-field
              v-model="filters.name"
              solo
              dense
              :label="$t('table_headers.name')"
              hide-details
              clearable
              @input="$emit('update:filter')"
            />
          </v-col>
          <v-col>
            <v-select
              v-model="filters.eventType"
              solo
              dense
              :label="$t('dropdown.event_type.title')"
              hide-details
              clearable
              item-value="type"
              item-text="name"
              :items="[{type: eventTypeTestSession, name: $t('event_type.test_session')}, {type: eventTypeReadiness, name: $t('event_type.readiness')}]"
              @input="$emit('update:filter')"
            />
          </v-col>
          <v-col>
            <v-menu
              v-model="filterDateFromActive"
              :close-on-content-click="false"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  :value="dateFromText"
                  class="custom-height-text-field"
                  :label="$t('date_from.label')"
                  solo
                  dense
                  clearable
                  hide-details
                  v-on="on"
                />
              </template>
              <v-date-picker
                v-model="filters.dateFrom"
                :first-day-of-week="1"
                no-title
                :max="$dayjs().format('YYYY-MM-DD')"
                @input="filterDateFromActive = false; $emit('update:filter')"
              />
            </v-menu>
          </v-col>
          <v-col>
            <v-menu
              v-model="filterDateToActive"
              :close-on-content-click="false"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  :value="dateToText"
                  class="custom-height-text-field"
                  :label="$t('date_to.label')"
                  solo
                  dense
                  clearable
                  hide-details
                  v-on="on"
                />
              </template>
              <v-date-picker
                v-model="filters.dateTo"
                :first-day-of-week="1"
                no-title
                :min="filters.dateFrom"
                :max="$dayjs().format('YYYY-MM-DD')"
                @input="filterDateToActive = false; $emit('update:filter')"
              />
            </v-menu>
          </v-col>
        </v-row>
        <v-select
          v-model="filters.diagnosticProtocolId"
          class="mt-2"
          solo
          dense
          clearable
          hide-details
          :placeholder="$t('dropdown.select_protocol.title')"
          item-value="id"
          item-text="name"
          :items="protocols"
          @input="$emit('update:filter')"
        />
      </v-card-title>
    </template>
    <template v-slot:actions>
      <v-card-actions
        v-if="events.pagination && events.pagination.last_page > 1"
        class="d-flex justify-center align-center py-5"
      >
        <pagination
          v-model="events.pagination.current_page"
          class="flex-grow-1"
          :last-page="events.pagination.last_page"
        />
      </v-card-actions>
    </template>
    <v-card-text class="pa-0">
      <div style="height: 1px">
        <v-progress-linear
          v-if="events.loading"
          indeterminate
          height="1px"
        />
      </div>
      <v-list
        v-if="events.data.length > 0"
        class="v-list--bordered px-6 ma-0"
        :disabled="events.loading"
      >
        <v-list-item
          v-for="t in events.data"
          :key="t.id"
          :disabled="!t.diagnostic_protocol"
          :class="{ 'v-list-item--active': events.selected && events.selected.id === t.id }"
          @click="$emit('select:event', t)"
        >
          <v-list-item-content>
            <v-list-item-title :class="{ 'font-weight-bold primary--text': events.selected && events.selected.id === t.id }">
              {{ t.title }} ({{ t.diagnostic_protocol ? t.diagnostic_protocol.name : $t('status.unknown') }})
            </v-list-item-title>
            <v-list-item-subtitle>
              {{ $dayjs(t.start_at).format(getDateTimeFormatForUser('DD/MM/YYYY HH:mm:ss')) }} &middot; {{ $tc("players_involved.count", t.player_ids.length, {count: t.player_ids.length}) }}
            </v-list-item-subtitle>
          </v-list-item-content>
          <v-list-item-action>
            <div class="d-flex align-center">
              <v-chip
                label
                :color="eventTypes[t.event_type].color"
                dark
                small
              >
                {{ eventTypes[t.event_type].text }}
              </v-chip>
            </div>
          </v-list-item-action>
        </v-list-item>
      </v-list>
      <div
        v-else
        class="text-center py-6 grey--text"
      >
        <div class="mb-3">
          {{ $t('card_dialog.select_test_session.label') }}
        </div>
      </div>
    </v-card-text>
  </card-dialog>
</template>

<script>
import CardDialog from '@/components/helpers/CardDialog'
import { EventType, EventTypes } from '@/data/models'
import { getDateTimeFormatForUser } from '@/utils/helpers'
import Pagination from '@/components/Pagination.vue'

export default {
  name: 'SelectTestSessionDialog',
  components: { Pagination, CardDialog },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    events: {
      type: Object,
      required: true
    },
    filters: {
      type: Object,
      required: true
    },
    protocols: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      filterDateFromActive: false,
      filterDateToActive: false
    }
  },
  computed: {
    teams() {
      return this.$store.getters[this.$storeGetters.APP_TEAMS]
    },
    dateFromText() {
      return this.$dayjs(this.filters.dateFrom).format(getDateTimeFormatForUser('D MMM, YYYY'))
    },
    dateToText() {
      return this.$dayjs(this.filters.dateTo).format(getDateTimeFormatForUser('D MMM, YYYY'))
    },
    modalVisible: {
      get: function () {
        return this.visible
      },
      set: function (value) {
        this.$emit('update:visible', value)
      }
    }
  },
  created() {
    this.eventTypeTestSession = EventType.TEST_SESSION
    this.eventTypeReadiness = EventType.READINESS

    this.eventTypes = EventTypes
  },
  methods: {
    getDateTimeFormatForUser
  }
}
</script>
